export type InScopeBreaker = "trustpilot" | "getHelp" | "bookEarly";
export type ViewMode = "desktopList" | "mobileList" | "desktopGrid";

export type BreakerFlags = Partial<Record<InScopeBreaker, true>>;

/** After card at this 0-based index (mobile: render after card; desktop Get Help: before next card). */
export type BreakerMap = Record<number, BreakerFlags>;

export type PageBreakerState = {
  shownInEarlierLists: Set<InScopeBreaker>;
};

export type PlacementContext = {
  viewMode: ViewMode;
  isChinaApp: boolean;
  getHelpDismissedForCity: boolean;
};

const PRIORITY: InScopeBreaker[] = ["trustpilot", "getHelp", "bookEarly"];

export const createPageBreakerState = (): PageBreakerState => ({
  shownInEarlierLists: new Set(),
});

const cardsAfterSlot = (listTotal: number, afterCardIndex: number): number =>
  listTotal - (afterCardIndex + 1);

const passesFr6 = (listTotal: number, afterCardIndex: number): boolean =>
  cardsAfterSlot(listTotal, afterCardIndex) >= 3;

const isGetHelpRepeatSlot = (afterCardIndex: number, viewMode: ViewMode): boolean => {
  const index = afterCardIndex + 1;
  if (index === 0) return false;
  if (viewMode === "desktopList" || viewMode === "desktopGrid") {
    return index % 30 === 0;
  }
  return index % 24 === 0 || index % 30 === 0;
};

const getGridSlotIndices = (listTotal: number): number[] =>
  [14, 23].filter((afterCardIndex) => passesFr6(listTotal, afterCardIndex));

const getLongListSlotIndices = (listTotal: number, viewMode: ViewMode): number[] => {
  if (viewMode === "desktopGrid") {
    return getGridSlotIndices(listTotal);
  }

  const slots = new Set<number>();
  if (passesFr6(listTotal, 17)) slots.add(17);
  if (viewMode === "desktopList" && passesFr6(listTotal, 23)) slots.add(23);

  const maxIndex = listTotal - 1;
  for (let afterCardIndex = 0; afterCardIndex <= maxIndex; afterCardIndex += 1) {
    const cardNumber = afterCardIndex + 1;
    let isGetHelpSlot = false;
    if (viewMode === "desktopList") {
      isGetHelpSlot = cardNumber % 30 === 0;
    } else {
      isGetHelpSlot = cardNumber % 24 === 0 || cardNumber % 30 === 0;
    }
    if (isGetHelpSlot && passesFr6(listTotal, afterCardIndex)) {
      slots.add(afterCardIndex);
    }
  }

  return Array.from(slots).sort((a, b) => a - b);
};

export const getSlotIndicesForListTotal = (listTotal: number, viewMode: ViewMode): number[] => {
  const total = Math.max(0, Math.floor(Number(listTotal) || 0));
  if (total <= 12) return [];

  if (viewMode === "desktopGrid") {
    return getGridSlotIndices(total);
  }

  if (total <= 17) return passesFr6(total, 9) ? [9] : [];
  if (total <= 19) {
    return [9, 14].filter((idx) => passesFr6(total, idx));
  }

  return getLongListSlotIndices(total, viewMode);
};

const preferredBreakerForSlot = (
  afterCardIndex: number,
  viewMode: ViewMode,
  isLongList: boolean,
): InScopeBreaker | "priority" => {
  if (!isLongList) return "priority";
  if (afterCardIndex === 17) return "trustpilot";
  if (afterCardIndex === 23 && viewMode === "desktopList") return "bookEarly";
  if (isGetHelpRepeatSlot(afterCardIndex, viewMode)) return "getHelp";
  return "priority";
};

const orderedCandidates = (
  afterCardIndex: number,
  viewMode: ViewMode,
  isLongList: boolean,
): InScopeBreaker[] => {
  const pref = preferredBreakerForSlot(afterCardIndex, viewMode, isLongList);
  if (pref === "priority") return [...PRIORITY];
  return [pref, ...PRIORITY.filter((b) => b !== pref)];
};

const canAssignBreaker = (
  breaker: InScopeBreaker,
  afterCardIndex: number,
  assignedInList: Set<InScopeBreaker>,
  pageState: PageBreakerState,
  ctx: PlacementContext,
): boolean => {
  if (breaker === "trustpilot" && ctx.isChinaApp) return false;
  if (breaker === "bookEarly" && ctx.viewMode !== "desktopList") return false;
  if (breaker === "getHelp" && ctx.getHelpDismissedForCity) return false;

  if (pageState.shownInEarlierLists.has(breaker)) return false;

  if (assignedInList.has(breaker)) {
    if (breaker === "getHelp" && isGetHelpRepeatSlot(afterCardIndex, ctx.viewMode)) {
      return true;
    }
    return false;
  }

  return true;
};

export const buildListBreakerMap = (
  listTotal: number,
  pageState: PageBreakerState,
  ctx: PlacementContext,
): { map: BreakerMap; breakersUsed: Set<InScopeBreaker> } => {
  const map: BreakerMap = {};
  const assignedInList = new Set<InScopeBreaker>();
  const breakersUsed = new Set<InScopeBreaker>();
  const isLongList = listTotal >= 20;
  const slotIndices = getSlotIndicesForListTotal(listTotal, ctx.viewMode);

  slotIndices.forEach((afterCardIndex) => {
    const candidates = orderedCandidates(afterCardIndex, ctx.viewMode, isLongList);
    const chosen = candidates.find((breaker) =>
      canAssignBreaker(breaker, afterCardIndex, assignedInList, pageState, ctx),
    );
    if (!chosen) return;

    map[afterCardIndex] = { ...(map[afterCardIndex] || {}), [chosen]: true };
    breakersUsed.add(chosen);
    if (chosen !== "getHelp" || !isGetHelpRepeatSlot(afterCardIndex, ctx.viewMode)) {
      assignedInList.add(chosen);
    } else if (!assignedInList.has("getHelp")) {
      assignedInList.add("getHelp");
    }
  });

  return { map, breakersUsed };
};

export type BuildListABPlanParams = {
  listATotal: number;
  listBTotal: number;
  pageState?: PageBreakerState;
  ctx: PlacementContext;
};

export const buildListABPlan = ({
  listATotal,
  listBTotal,
  pageState = createPageBreakerState(),
  ctx,
}: BuildListABPlanParams): {
  listA: BreakerMap;
  listB: BreakerMap;
  stateAfterAB: PageBreakerState;
} => {
  const { map: listA, breakersUsed: usedA } = buildListBreakerMap(listATotal, pageState, ctx);
  const stateAfterA: PageBreakerState = {
    shownInEarlierLists: new Set([...pageState.shownInEarlierLists, ...usedA]),
  };
  const { map: listB } = buildListBreakerMap(listBTotal, stateAfterA, ctx);
  const usedB = new Set<InScopeBreaker>();
  Object.values(listB).forEach((flags) => {
    (Object.keys(flags) as InScopeBreaker[]).forEach((b) => usedB.add(b));
  });

  return {
    listA,
    listB,
    stateAfterAB: {
      shownInEarlierLists: new Set([...stateAfterA.shownInEarlierLists, ...usedB]),
    },
  };
};

export type BuildListCPlanParams = {
  listCTotal: number;
  pageState?: PageBreakerState;
  ctx: PlacementContext;
};

export const buildListCPlan = ({
  listCTotal,
  pageState = createPageBreakerState(),
  ctx,
}: BuildListCPlanParams): { listC: BreakerMap } => {
  if (listCTotal <= 0) return { listC: {} };
  const { map: listC } = buildListBreakerMap(listCTotal, pageState, ctx);
  return { listC };
};

export const getFlagsForLocalIndex = (
  map: BreakerMap,
  localIndex: number,
): BreakerFlags | undefined => map[localIndex];

export const getBreakerMapForSegment = (
  plans: { listA: BreakerMap; listB: BreakerMap } | null,
  isListB: boolean,
): BreakerMap | null => {
  if (!plans) return null;
  return isListB ? plans.listB : plans.listA;
};

export const resolveBreakerViewMode = (
  preferred: ViewMode | undefined,
  isGridView: boolean,
  isDesktop: boolean,
): ViewMode => {
  if (preferred) return preferred;
  if (isGridView) return "desktopGrid";
  if (isDesktop) return "desktopList";
  return "mobileList";
};

/** Desktop list: Get Help after card N → render before card at localIndex N+1. */
export const shouldShowGetHelpBeforeCard = (map: BreakerMap, localIndex: number): boolean =>
  Boolean(map[localIndex - 1]?.getHelp);

/** After rendering card at localIndex (mobile + desktop TP/BE). */
export const shouldShowAfterCard = (
  map: BreakerMap,
  localIndex: number,
  breaker: InScopeBreaker,
): boolean => Boolean(map[localIndex]?.[breaker]);
